import React, { useRef, useState, useContext, useEffect } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Col, Container, Modal, Row } from "react-bootstrap";
import { useHistory, useLocation } from "react-router";
import { useTranslation } from "react-i18next";
import { Nav, Tab } from "react-bootstrap";
import styled from "styled-components";
import getUnixTime from "date-fns/getUnixTime";
import { FormProvider, useForm } from "react-hook-form";

import { Card, CardBody } from "@/shared/components/Card";
import FormHeader from "../../../shared/components/form/FormHeader";
import { NavItem, NavLink, TabsWrap } from "@/shared/components/Tabs";
import { FormContainer } from "../../../shared/components/form/FormElements";
import { scrollbarStyles } from "../../../utils/palette";
import { addMsg } from "../../../features/snackbar/snackbarSlice";
import {
  getToken,
  getVehicles,
  logErrors,
  post,
  postApi,
  urlConfig,
} from "../../../api/getUser";
import InfoPopup from "../../../shared/components/InfoPopup";
import {
  getDisplayDate,
  getDistanceFactor,
  isObjEmpty,
  uploadDocsToS3,
} from "../../../shared/helpers";
import VehicleDetailsForm from "./VehicleDetailsForm";
import UsersAssignedForm from "./UsersAssignedForm";
import DocumentsForm from "../../UserManagement/DocumentsForm";
import VehicleSpecsForm from "./VehicleSpecsForm";
import { useAuth } from "../../../contexts/AuthContext";
import { auth } from "../../../api/firebase";
import axios from "axios";
import IntegrationsForm from "./IntegrationsForm";
import ActionPopup from "../../../shared/components/ActionPopup";
import { updateVehiclesList } from "../../../features/vehicles/vehiclesSlice";

const VehiclesForm = () => {
  const history = useHistory();
  const location = useLocation();
  const dispatch = useDispatch();
  const { t } = useTranslation("common");

  const routeData = location?.state?.data;
  const rowData = routeData?.rowData;
  const screen = location?.state?.screen;
  const type = location?.state?.type;

  const { orgId, roleId } = useSelector((state) => state.user);
  const { distance } = useSelector((state) => state.units);

  //let vehicleInfoRef = useRef({});
  let licenseInfo = useRef({});

  const methods = useForm();
  const { watch, setValue } = methods;
  const { errors } = methods.formState;

  const vehicleName = watch("vehicleName");
  const licenseNo = watch("licenseNo");
  const vehicleType = watch("vehicleType");
  const vin = watch("vin");
  const status = watch("status");
  const make = watch("make");
  const model = watch("model");
  const year = watch("year");
  const group = watch("group");
  const fuelType = watch("fuelType");
  const primaryMeter = watch("primaryMeter");
  const startingOdo = watch("startingOdo");
  const purchasePrice = watch("purchasePrice");
  const outOfService = watch("outOfService");
  const recommendMaintSchedule = watch("recommendMaintSchedule");
  const currentOdo = watch("currentOdo");
  const assetCondition = watch("assetCondition");

  const telematicsDataSync = watch("telematicsDataSync");

  const [documents, setDocuments] = useState([]);
  const [docsIncomplete, setDocsIncomplete] = useState(false);

  //State to check if any document has been changed to updated last updated date
  const [docUpdated, setDocUpdated] = useState(false);

  const [saveBtnLoading, setSaveBtnLoading] = useState(false);

  //For showing info popup during save
  const [addVehicleFailedData, setAddVehicleFailedData] = useState({
    message: "",
    icon: { className: "fi fi-rr-triangle-warning", color: "red" },
  });
  const [addVehicleFailed, setAddVehicleFailed] = useState(false);

  //State for users assigned
  const [usersAssigned, setUsersAssigned] = useState([]);
  //State to check if users assigned has been changed
  const [userAssignedUpdated, setUserAssignedUpdated] = useState(false);

  //to store selected users assigned on edit
  //This is needed because on change of group in edit vehicle, existing users assigned are not changed
  //const usersAssignedOnEdit = useRef([]);

  //Ref for VehicleSpecsForm Data
  const vehicleSpecsInfoRef = useRef(null);

  const [activeKey, setActiveKey] = useState("1");

  const [vehiclePhoto, setVehiclePhoto] = useState("");

  //flag to check whether or not to call update int ids
  const telematicsFlagActivated = useRef(false);

  //State flag to check if no integration id is found
  const [showNoIntegrationIdPopup, setShowNoIntegrationIdPopup] =
    useState(false);

  //To show a warning popup on vehicle save when current odometer is changed
  const [showCurrentOdoWarnPopup, setShowCurrentOdoWarnPopup] = useState("");

  //odo warning message to show below current odo field
  const [currentOdoWarnMsg, setCurrentOdoWarnMsg] = useState("");

  //Ref flag to check if odo has been overridden
  const isOdoOverridden = useRef(false);

  const checkDocsIncomplete = () => {
    for (const doc of documents) {
      if (!doc.docName || !doc.image) {
        return true;
      }
    }
    return false;
  };

  //Cancel click in odo override change warning popup
  const handleCancelSave = () => {
    const distanceFactor = getDistanceFactor(primaryMeter?.value, distance);
    const currentOdoConverted = rowData?.odo
      ? (rowData.odo / distanceFactor)?.toFixed(2)
      : 0;
    setValue("currentOdo", currentOdoConverted);
    setCurrentOdoWarnMsg("");
    setShowCurrentOdoWarnPopup(false);
  };

  const updateIntegrationIdScript = async (vehicleId, vehicleName, vin) => {
    try {
      const apiData = {
        querystring: "update_integrations_id_for_vehicle",
        veh_id: vehicleId,
        name: vehicleName,
        vin: vin || "",
      };
      const addIntegrationIdRes = await post("integrations", apiData);
      if (
        addIntegrationIdRes?.success === 1
        // &&
        // addIntegrationIdRes?.message == "no_integration_id"
      ) {
        return 1;
      }
      return 0;
    } catch (error) {
      console.log(error);
      return 0;
    }
  };

  const handleSaveVehicle = async () => {
    try {
      const vehicleId =
        type === "edit" ? rowData.veh_id : `${orgId}_${Date.now()}`;

      setSaveBtnLoading(true);

      //Check if vehicle already exists
      const token = await getToken(); //sessionStorage.getItem("token");
      const email = auth?.currentUser?.email;
      const config = {
        headers: { Authorization: `Bearer ${token}` },
      };

      const uniqueVehicleApiRes = await axios.post(
        urlConfig.commonNew,
        {
          email,
          querystring: "check_vehicle_exist",
          operation: type === "edit" ? "update" : "create",
          veh_id: vehicleId,
          name: vehicleName ? vehicleName : "",
          vin_no: vin ? vin : "",
          license_no: licenseNo ? licenseNo : "",
          asset_type: 0,
        },
        config,
      );

      const resData = uniqueVehicleApiRes.data;
      // if (resData.success) {
      const { data } = resData;
      let errMsg = "";
      if (data == 1 || data == 2 || data == 3 || data == 4) {
        if (data == 1) {
          errMsg = t("vehicle_limit_reached_err");
        } else if (data == 2) {
          errMsg = t("duplicate_entry_veh_name_err");
        } else if (data == 3) {
          errMsg = t("duplicate_entry_veh_vin_err");
        } else if (data == 4) {
          errMsg = t("duplicate_entry_veh_license_err");
        }
        setAddVehicleFailedData((prev) => ({
          ...prev,
          message: errMsg,
        }));
        setAddVehicleFailed(true);
        setSaveBtnLoading(false);
        return;
      }
      // }

      //call update_integration_id_script if telematics flag is enabled
      if (telematicsFlagActivated.current) {
        const integrationIdRes = await updateIntegrationIdScript(
          vehicleId,
          vehicleName,
          vin,
        );
        if (!integrationIdRes) {
          setSaveBtnLoading(false);
          setShowNoIntegrationIdPopup(true);
          return;
        }
      }

      const { defaultData, additionalData } = vehicleSpecsInfoRef?.current;

      const {
        width,
        height,
        length,
        weight,
        cargoVol,
        maxPayload,
        frontTireInfo,
        frontTirePSI,
        rearTireInfo,
        rearTirePSI,
        fuelQuality,
        oil,
        oilCapacity,
        fuelTankCapacity,
      } = defaultData;

      let vehicleSpecsFieldsString = "";
      additionalData?.forEach((f) => {
        vehicleSpecsFieldsString += `${f.title}~~${f.author}:::`;
      });
      vehicleSpecsFieldsString = vehicleSpecsFieldsString?.slice(
        0,
        vehicleSpecsFieldsString?.length - 3,
      );

      let docs = { All_Documents: [] };

      let imagesToS3 = [];

      documents?.forEach((d) => {
        const currentDate = new Date();
        let items = [];
        if (d.imgUpdated) {
          items.push({
            "Updated date": getDisplayDate(getUnixTime(currentDate)),
          });
        }
        d.fields.forEach((f) => {
          items.push({ [f.title]: f.author ? f.author : "" });
        });

        let imageName = `${d.docName}_${getUnixTime(currentDate)}.${
          d.image.extension
        }`;

        let temp = {};
        temp[d.docName] = {
          items: items,
          image: d.image.file ? imageName : d.image.type,
          visible: d.isVisible,
          //image_format: d.image.imgFormat,
        };
        docs.All_Documents.push(temp);

        if (d.image.file) {
          imagesToS3.push({
            file: d.image.file,
            key: `image_data/org_${orgId}/vehicle_${vehicleId}/vehicle_doc/${imageName}`,
          });
        }
      });

      const distanceFactor = getDistanceFactor(primaryMeter?.value, distance);
      let startingodoConverted = startingOdo ? startingOdo * distanceFactor : 0;

      //override odo and override date
      //let overrideOdo = 0;
      //let overrideDate = 0;

      // if (type === "edit") {
      //   overrideOdo = +rowData.odo_override;
      //   overrideDate = +rowData.odo_override_date;
      //   const overrideOdoConverted = currentOdo
      //     ? currentOdo * distanceFactor
      //     : 0;
      //   if (+overrideOdoConverted !== +rowData?.odo_override) {
      //     overrideOdo = overrideOdoConverted;
      //     overrideDate = getUnixTime(new Date());
      //   }
      // }

      const apiData = {
        vehicle_name: vehicleName,
        make: make ? make : "",
        model: model ? model : "",
        veh_type: vehicleType ? vehicleType?.value : "",
        veh_active: status ? 1 : 0,
        secondary_status: outOfService ? 1 : 0,
        year: year ? year : 0,
        license_no: licenseNo ? licenseNo : "",
        vin_no: vin ? vin : "",
        veh_img: vehiclePhoto ? vehiclePhoto.imgName : "",
        starting_odo: startingodoConverted,
        primary_meter: primaryMeter ? primaryMeter?.value : "",
        custom_specs: vehicleSpecsFieldsString,
        width: width ? width : "",
        length: length ? length : "",
        height: height ? height : "",
        weight: weight ? weight : "",
        cargo_value: cargoVol ? cargoVol : "",
        max_payload: maxPayload ? maxPayload : "",
        front_tyre_info: frontTireInfo ? frontTireInfo : "",
        front_tyre_psi: frontTirePSI ? frontTirePSI : "",
        rear_tyre_info: rearTireInfo ? rearTireInfo : "",
        rear_tyre_psi: rearTirePSI ? rearTirePSI : "",
        fuel_type: fuelType ? fuelType?.value : "",
        additional_documents: documents?.length > 0 ? JSON.stringify(docs) : "",
        fuel_quality: fuelQuality ? fuelQuality : "",
        oil: oil ? oil : "",
        oil_capacity: oilCapacity ? oilCapacity : "",
        fuel_tank_capacity: fuelTankCapacity ? fuelTankCapacity : "",
        action: type === "edit" ? 2 : 1,
        operation: type === "edit" ? "update" : "create",
        sync_version: "v2",
        veh_id: vehicleId,
        group_id: group ? group?.group_id : "",
        purchase_price: purchasePrice ? purchasePrice : 0,
        provider_type_1: telematicsDataSync ? 1 : 0,
        asset_type: 0,
        asset_condition: assetCondition ? assetCondition.value : 0,
        asset_condition_updated_at:
          assetCondition?.value !== rowData?.asset_condition
            ? getUnixTime(new Date())
            : rowData?.asset_condition_updated_at || 0,
      };

      //Add override values if override odo has changed
      if (type === "edit" && roleId === 1 && isOdoOverridden.current) {
        // const overrideOdoConverted = currentOdo
        //   ? (currentOdo * distanceFactor)?.toFixed(2)
        //   : 0;
        if (+currentOdo !== +rowData?.odo) {
          apiData.odo_override = +currentOdo;
          apiData.odo_override_date = getUnixTime(new Date());
        }
      }

      const userssAssignedIds = usersAssigned.map((u) => u.user_id);
      const userApiData = {
        querystring: "vehicle_operator_update",
        operator_id: userssAssignedIds,
        veh_id: vehicleId,
        vehicle_operation: "update",
      };

      const response = await post("vehicle", apiData);
      if (userAssignedUpdated) {
        const vehResponse = await postApi(userApiData, "commonNew");
      }

      if (!response?.success) {
        const { code } = response;
        if (code == 1) {
          setAddVehicleFailedData((prev) => ({
            ...prev,
            message: t("vehicle_limit_reached_err"),
          }));
        } else if (code == 2) {
          setAddVehicleFailedData((prev) => ({
            ...prev,
            message: t("duplicate_entry_veh_name_err"),
          }));
        } else if (code == 3) {
          setAddVehicleFailedData((prev) => ({
            ...prev,
            message: t("duplicate_entry_veh_vin_err"),
          }));
        } else if (code == 4) {
          setAddVehicleFailedData((prev) => ({
            ...prev,
            message: t("duplicate_entry_veh_license_err"),
          }));
        } else if (code == 5) {
          setAddVehicleFailedData((prev) => ({
            ...prev,
            message: t("duplicate_entry_veh_err"),
          }));
        } else {
          setAddVehicleFailedData((prev) => ({
            ...prev,
            message: t("form_err_msg"),
          }));
        }
        setSaveBtnLoading(false);
        setAddVehicleFailed(true);
        return;
      }
      if (response?.success) {
        //Update vehicles list in redux
        const updatedVehiclesList = await getVehicles();
        dispatch(updateVehiclesList(updatedVehiclesList));

        //log api error if recommend maint schedule is checked
        if (recommendMaintSchedule) {
          const errLogData = {
            url: "AI schedule requested",
            method_type: vehicleId,
            error: "",
            request: "",
            placeholder: "",
          };
          await logErrors(errLogData);
        }
        const snackbarMsg =
          type === "edit"
            ? t("record_edited_msg", { recordName: t("vehicle") })
            : t("record_added_msg", { recordName: t("vehicle") });
        // let imagesToS3 = [];
        if (vehiclePhoto && vehiclePhoto.file) {
          imagesToS3.push({
            file: vehiclePhoto.file,
            key: `image_data/org_${orgId}/vehicle_${vehicleId}/${vehiclePhoto.imgName}`,
          });
        }

        if (imagesToS3?.length > 0) {
          const docsUploaded = await uploadDocsToS3(imagesToS3);
          if (docsUploaded) {
            if (!recommendMaintSchedule) {
              dispatch(addMsg(snackbarMsg));
            }
            setSaveBtnLoading(false);
            history.push("/assets/vehicles", {
              fromCancel: false,
              recommendMaintSchedule: type !== "edit" && recommendMaintSchedule,
              newVehicleId:
                type !== "edit" && recommendMaintSchedule ? vehicleId : "",
            });
          }
        } else {
          if (!recommendMaintSchedule) {
            dispatch(addMsg(snackbarMsg));
          }
          setSaveBtnLoading(false);
          history.push("/assets/vehicles", {
            fromCancel: false,
            recommendMaintSchedule: type !== "edit" && recommendMaintSchedule,
            newVehicleId:
              type !== "edit" && recommendMaintSchedule ? vehicleId : "",
          });
        }
      }
    } catch (e) {
      console.log(e);
      setAddVehicleFailedData((prev) => ({
        ...prev,
        message: t("form_err_msg"),
      }));
      setSaveBtnLoading(false);
      setAddVehicleFailed(true);
      return;
    }
  };

  const onSubmit = async () => {
    try {
      setDocuments((prev) => {
        let temp = [...prev];
        for (let doc of temp) {
          if (!doc.docName || !doc.image) {
            doc.isError = true;
          } else {
            doc.isError = false;
          }
        }
        return temp;
      });
      const docsIncomplete = checkDocsIncomplete();
      if (docsIncomplete) {
        setDocsIncomplete(true);
        return;
      }

      if (
        type === "edit" &&
        roleId === 1 &&
        +rowData.odo !== +currentOdo &&
        isOdoOverridden.current
      ) {
        setShowCurrentOdoWarnPopup(true);
      } else {
        await handleSaveVehicle();
      }
    } catch (e) {
      console.log(e);
      setAddVehicleFailedData((prev) => ({
        ...prev,
        message: t("form_err_msg"),
      }));
      setSaveBtnLoading(false);
      setAddVehicleFailed(true);
      return;
    }
  };

  return (
    <FormProvider {...methods}>
      {/* {showUserAddedPopup && (
        <UserAddedPopup
          showPopup={showUserAddedPopup}
          setShowPopup={setShowUserAddedPopup}
          userAdded={userAdded}
          setUserAdded={setUserAdded}
          userData={userData}
        />
      )} */}
      {docsIncomplete && (
        <InfoPopup
          message={t("doc_tab_incomplete_err_msg")}
          icon={{ className: "fi fi-rr-triangle-warning", color: "red" }}
          showPopup={docsIncomplete}
          setShowPopup={setDocsIncomplete}
        />
      )}
      {addVehicleFailed && (
        <InfoPopup
          message={addVehicleFailedData.message}
          icon={addVehicleFailedData.icon}
          showPopup={addVehicleFailed}
          setShowPopup={setAddVehicleFailed}
        />
      )}
      {showCurrentOdoWarnPopup && (
        <ActionPopup
          showPopup={showCurrentOdoWarnPopup}
          setShowPopup={setShowCurrentOdoWarnPopup}
          handleNo={handleCancelSave}
          handleYes={handleSaveVehicle}
          message={t("you_are_overriding_current_odo_warn_msg")}
          yesBtnLoading={saveBtnLoading}
          yesBtnText={t("confirm")}
          noBtnText={t("cancel")}
        />
      )}
      {showNoIntegrationIdPopup && (
        <InfoPopup
          message={t("no_integration_id_popup_msg")}
          icon={{ className: "fi fi-rr-triangle-warning", color: "red" }}
          showPopup={showNoIntegrationIdPopup}
          setShowPopup={setShowNoIntegrationIdPopup}
          actionIcon={{
            label: t("okay"),
            handler: () => {
              setActiveKey("5");
              setValue("telematicsDataSync", false);
              telematicsFlagActivated.current = false;
              setShowNoIntegrationIdPopup(false);
            },
          }}
        />
      )}
      <FormContainer onSubmit={methods.handleSubmit(onSubmit)}>
        <FormHeader
          handleCancel={() =>
            history.push("/assets/vehicles", {
              fromCancel: true,
              recommendMaintSchedule: false,
              newVehicleId: "",
            })
          }
          //disableSave={!vehicle}
          isSaveLoading={saveBtnLoading}
          title={type === "edit" ? t("edit_vehicle") : t("add_vehicle")}
          //handleCancel={handleCancel}
        />
        <Container>
          <Row>
            <Col xs={12} md={12} lg={12} xl={12}>
              <Card>
                <CardBody
                  style={{
                    padding: 0,
                    //height: `${window.innerHeight - 150}px`,
                    //overflow: "auto",
                  }}
                >
                  <TabsWrap>
                    <Tabs
                      rowData={rowData}
                      type={type}
                      //vehicleInfoRef={vehicleInfoRef}
                      licenseInfo={licenseInfo}
                      documents={documents}
                      setDocuments={setDocuments}
                      docUpdated={docUpdated}
                      setDocUpdated={setDocUpdated}
                      errors={errors}
                      usersAssigned={usersAssigned}
                      setUsersAssigned={setUsersAssigned}
                      userAssignedUpdated={userAssignedUpdated}
                      setUserAssignedUpdated={setUserAssignedUpdated}
                      vehicleSpecsInfoRef={vehicleSpecsInfoRef}
                      activeKey={activeKey}
                      setActiveKey={setActiveKey}
                      vehiclePhoto={vehiclePhoto}
                      setVehiclePhoto={setVehiclePhoto}
                      telematicsFlagActivated={telematicsFlagActivated}
                      isOdoOverridden={isOdoOverridden}
                      currentOdoWarnMsg={currentOdoWarnMsg}
                      setCurrentOdoWarnMsg={setCurrentOdoWarnMsg}
                    />
                  </TabsWrap>
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </FormContainer>
    </FormProvider>
  );
};

export default VehiclesForm;

const Tabs = ({
  rowData,
  type,
  //vehicleInfoRef,
  documents,
  setDocuments,
  licenseInfo,
  docUpdated,
  setDocUpdated,
  errors,
  usersAssigned,
  setUsersAssigned,
  userAssignedUpdated,
  setUserAssignedUpdated,
  vehicleSpecsInfoRef,
  activeKey,
  setActiveKey,
  vehiclePhoto,
  setVehiclePhoto,
  telematicsFlagActivated,
  isOdoOverridden,
  currentOdoWarnMsg,
  setCurrentOdoWarnMsg,
}) => {
  const { t } = useTranslation("common");

  const { fetchTelematicsOdo, integrationsData } = useAuth();

  const showIntegrationsTab = integrationsData.find(
    (data) =>
      data.provider_type === 1 &&
      ((data.service_type === 1 && data.service_enabled === 1) ||
        (data.service_type === 2 && data.service_enabled === 1) ||
        (data.service_type === 4 && data.service_enabled === 1)),
  )
    ? true
    : false;

  return (
    <Tab.Container activeKey={activeKey} onSelect={(e) => setActiveKey(e)}>
      <TabsWrap>
        <Nav className="nav-tabs">
          <NavItem
            id="vehicle-details-tab"
            $active={activeKey == "1"}
            // style={{
            //   background: activeKey !== "1" ? "aliceblue" : "none",
            // }}
          >
            <NavLink eventKey="1" name="vehicle-details-link">
              {t("vehicle_details")}{" "}
              {!isObjEmpty(errors) && (
                <i
                  className="fi fi-rr-triangle-warning"
                  style={{
                    color: "red",
                    position: "relative",
                    top: "2px",
                  }}
                ></i>
              )}
            </NavLink>
          </NavItem>
          <NavItem
            id="vehicle-specs-tab"
            $active={activeKey == "2"}
            // style={{
            //   background: activeKey !== "2" ? "aliceblue" : "none",
            // }}
          >
            <NavLink eventKey="2" name="vehicle-specs-link">
              {t("vehicle_specifications")}
            </NavLink>
          </NavItem>
          <NavItem
            id="documents-tab"
            $active={activeKey == "3"}
            // style={{
            //   background: activeKey !== "3" ? "aliceblue" : "none",
            // }}
          >
            <NavLink eventKey="3" name="documents-link">
              {documents.length > 0
                ? t("count_documents_added", {
                    count: documents?.length,
                  })
                : t("documents")}
            </NavLink>
          </NavItem>
          <NavItem
            id="users-assigned-tab"
            $active={activeKey == "4"}
            // style={{
            //   background: activeKey !== "4" ? "aliceblue" : "none",
            // }}
          >
            <NavLink eventKey="4" id={"users-assigned-link"}>
              {usersAssigned?.length > 0
                ? t("count_users_assigned", {
                    count: usersAssigned?.length,
                  })
                : t("user_assignment")}
            </NavLink>
          </NavItem>

          {showIntegrationsTab && (
            <NavItem
              id="integrations-tab"
              $active={activeKey == "5"}
              // style={{
              //   background: activeKey !== "5" ? "aliceblue" : "none",
              // }}
            >
              <NavLink eventKey="5" id={"users-assigned-link"}>
                {t("integrations")}
              </NavLink>
            </NavItem>
          )}
        </Nav>

        <Tab.Content>
          <TabPane
            eventKey="1"
            // style={{
            //   height: `${window.innerHeight - 200}px`,
            //   overflow: "auto",
            // }}
          >
            <VehicleDetailsForm
              rowData={rowData}
              type={type}
              //vehicleInfoRef={vehicleInfoRef}
              setUsersAssigned={setUsersAssigned}
              userAssignedUpdated={userAssignedUpdated}
              setUserAssignedUpdated={setUserAssignedUpdated}
              vehiclePhoto={vehiclePhoto}
              setVehiclePhoto={setVehiclePhoto}
              isOdoOverridden={isOdoOverridden}
              currentOdoWarnMsg={currentOdoWarnMsg}
              setCurrentOdoWarnMsg={setCurrentOdoWarnMsg}
            />
          </TabPane>
          <TabPane eventKey="2">
            <VehicleSpecsForm
              rowData={rowData}
              type={type}
              //vehicleInfoRef={vehicleInfoRef}
              vehicleSpecsInfoRef={vehicleSpecsInfoRef}
              //setVehicleSpecsInfo={setVehicleSpecsInfo}
              //vehicleSpecsFields={vehicleSpecsFields}
              //setVehicleSpecsFields={setVehicleSpecsFields}
            />
          </TabPane>
          <TabPane
            eventKey="3"
            // style={{
            //   height: `${window.innerHeight - 198}px`,
            //   overflow: "auto",
            // }}
          >
            <DocumentsForm
              rowData={rowData}
              type={type}
              documents={documents}
              setDocuments={setDocuments}
              licenseInfo={licenseInfo}
              docUpdated={docUpdated}
              setDocUpdated={setDocUpdated}
            />
          </TabPane>
          <TabPane eventKey="4">
            <UsersAssignedForm
              usersAssigned={usersAssigned}
              setUsersAssigned={setUsersAssigned}
              userAssignedUpdated={userAssignedUpdated}
              setUserAssignedUpdated={setUserAssignedUpdated}
              type={type}
              rowData={rowData}
              assetType={"vehicle"}
            />
          </TabPane>

          <TabPane eventKey="5">
            <IntegrationsForm
              type={type}
              rowData={rowData}
              telematicsFlagActivated={telematicsFlagActivated}
            />
          </TabPane>
        </Tab.Content>
      </TabsWrap>
    </Tab.Container>
  );
};

export const TabPane = styled(Tab.Pane)`
  ${scrollbarStyles};
  height: calc(var(--window-height, 100vh) - 180px);
  overflow: auto;
  //padding: 1rem;
`;
